import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Ticket } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { api, resolveImg } from "../../lib/api";

const money = (n) => `$${Number(n).toFixed(0)}`;

const BuyModal = ({ event, onClose }) => {
  const available = event.tiers.filter((t) => t.remaining > 0);
  const [tierId, setTierId] = useState(available[0]?.id || event.tiers[0]?.id);
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const tier = event.tiers.find((t) => t.id === tierId);
  const maxQty = Math.min(tier?.remaining || 1, 20);

  const checkout = async () => {
    if (!name || !email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        event_id: event.id,
        tier_id: tierId,
        quantity: qty,
        buyer_name: name,
        buyer_email: email,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Try again.");
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="buy-modal"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
        className="relative w-full max-w-lg bg-ink border border-gold/25 rounded-sm p-8 max-h-[90vh] overflow-y-auto"
      >
        <button onClick={onClose} data-testid="buy-modal-close" className="absolute top-5 right-5 text-white/50 hover:text-gold">
          <X size={22} />
        </button>
        <span className="text-[11px] uppercase tracking-[0.3em] text-gold/90">Get Tickets</span>
        <h3 className="mt-2 font-serif text-3xl font-light">{event.title}</h3>
        <p className="mt-1 text-white/50 text-sm">{event.date} · {event.venue}</p>

        <div className="mt-6 space-y-3">
          {event.tiers.map((t) => {
            const out = t.remaining <= 0;
            return (
              <button
                key={t.id}
                disabled={out}
                onClick={() => { setTierId(t.id); setQty(1); }}
                data-testid={`tier-select-${t.name.toLowerCase().replace(/\s+/g,'-')}`}
                className={`w-full flex items-center justify-between px-5 py-4 border rounded-sm text-left transition-all duration-300 ${
                  tierId === t.id ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/40"
                } ${out ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <div>
                  <div className="text-white">{t.name}</div>
                  <div className="text-xs text-white/45">{out ? "Sold out" : `${t.remaining} left`}</div>
                </div>
                <div className="font-serif text-2xl text-gold">{money(t.price)}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <span className="text-sm text-white/60 uppercase tracking-[0.15em]">Quantity</span>
          <div className="flex items-center gap-4">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 border border-white/20 rounded-sm flex items-center justify-center hover:border-gold text-white/80" data-testid="qty-minus"><Minus size={16} /></button>
            <span className="w-8 text-center text-lg" data-testid="qty-value">{qty}</span>
            <button onClick={() => setQty((q) => Math.min(maxQty, q + 1))} className="w-9 h-9 border border-white/20 rounded-sm flex items-center justify-center hover:border-gold text-white/80" data-testid="qty-plus"><Plus size={16} /></button>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="buyer-name"
            className="w-full bg-transparent border-b border-white/20 py-3 focus:border-gold focus:outline-none placeholder:text-white/30" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email for tickets" data-testid="buyer-email"
            className="w-full bg-transparent border-b border-white/20 py-3 focus:border-gold focus:outline-none placeholder:text-white/30" />
        </div>

        <div className="mt-7 flex items-center justify-between">
          <div>
            <div className="text-xs text-white/45 uppercase tracking-[0.15em]">Total</div>
            <div className="font-serif text-3xl text-gold">${(tier ? tier.price * qty : 0).toFixed(2)}</div>
          </div>
          <button onClick={checkout} disabled={loading || !tier || tier.remaining <= 0} data-testid="checkout-button"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-black text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-gold-light transition-colors duration-500 disabled:opacity-50">
            <Ticket size={16} /> {loading ? "Redirecting..." : "Checkout"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Events = () => {
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  if (events.length === 0) return null;

  return (
    <section id="events" data-testid="events-section" className="relative py-28 lg:py-40 bg-ink">
      <div className="max-w-[88rem] mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <Reveal><Overline>Upcoming Events</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
                Reserve your <span className="text-gold-gradient italic">seat</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-white/55 max-w-sm leading-relaxed">
              Secure tickets to Teresa's upcoming productions, concerts, and conferences — checkout is fast and secure.
            </p>
          </Reveal>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {events.map((ev, i) => (
            <Reveal key={ev.id} delay={(i % 2) * 0.08}>
              <article data-testid={`event-card-${i}`} className="group relative overflow-hidden rounded-sm border border-white/10 bg-[#0b0b0b] h-full flex flex-col sm:flex-row">
                <div className="relative sm:w-2/5 h-56 sm:h-auto overflow-hidden shrink-0">
                  {ev.image_url ? (
                    <img src={resolveImg(ev.image_url)} alt={ev.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gold/20 to-transparent">
                      <Ticket className="text-gold/40" size={48} />
                    </div>
                  )}
                  {ev.featured && <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] bg-gold text-black px-2 py-1 rounded-sm">Featured</span>}
                </div>
                <div className="flex-1 p-7 flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-gold/80">{ev.date}</span>
                  <h3 className="mt-2 font-serif text-2xl lg:text-3xl font-light tracking-tight">{ev.title}</h3>
                  <p className="mt-1 text-white/45 text-sm">{ev.venue}</p>
                  <p className="mt-3 text-white/55 text-sm leading-relaxed line-clamp-3">{ev.description}</p>
                  <div className="mt-auto pt-5 flex items-center justify-between">
                    <div className="text-sm text-white/60">
                      {ev.tiers.length > 0 && <>from <span className="text-gold font-serif text-lg">{money(Math.min(...ev.tiers.map((t) => t.price)))}</span></>}
                    </div>
                    {ev.sold_out ? (
                      <span className="px-6 py-3 border border-white/20 text-white/40 text-xs uppercase tracking-[0.2em] rounded-sm" data-testid={`sold-out-${i}`}>Sold Out</span>
                    ) : (
                      <button onClick={() => setActive(ev)} data-testid={`get-tickets-${i}`}
                        className="px-6 py-3 bg-gold text-black text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-gold-light transition-colors duration-500">
                        Get Tickets
                      </button>
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <BuyModal event={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
};
